// Lib
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { get, isFunction } from 'lodash/fp';
import { connect } from 'react-redux';

// Components
import Button from '../../../../../../components/buttons/Button';
import Icon from '../../../../../../components/icons/Icon';

// Constants
import { WEB_CLIPPER_DOWNLOAD_URL } from '../../../../../../../common/utils/urlConstants';
import { IMAGE_SEARCH_POPUP_WEB_EXTENSION_ALERT_KEY } from './imageSearchPopupConstants';
import { TIMES } from '../../../../../../../common/utils/timeUtil';

// Styles
import './ImageSearchWebExtensionAlertPanel.scss';

// Utils
import { isPlatformIpad } from '../../../../../../platform/utils/platformDetailsUtils';
import { getUserAgent } from '../../../../../../platform/utils/platformBrowserUtils';
import useLocalStorage from '../../../../../../utils/services/localStorage/useLocalStorage';
import getClientConfig from '../../../../../../utils/getClientConfig';
import { createStructuredSelector } from 'reselect';
import { getCurrentUser } from '../../../../../../user/currentUserSelector';
import { getUserBrowserExtensionsSettings } from '../../../../../../../common/users/utils/userPropertyUtils';
import { propIn } from '../../../../../../../common/utils/immutableHelper';
import { safelyGetUserAgentBrowserName } from '../../../../../../../common/utils/userAgentUtil';
import { getPlatformDetailsSelector } from '../../../../../../platform/platformSelector';

const ExtensionPanelCloseButton = ({ closeFn }) => {
    return (
        <Button onClickFn={closeFn} className="extension-panel-close-button">
            <Icon name="close-x" />
        </Button>
    );
};

ExtensionPanelCloseButton.propTypes = {
    closeFn: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
    currentUser: getCurrentUser,
    platformDetails: getPlatformDetailsSelector,
});

const ImageSearchWebExtensionAlertPanel = ({ currentUser, platformDetails }) => {
    const [isPanelVisible, setIsPanelVisible] = useLocalStorage(IMAGE_SEARCH_POPUP_WEB_EXTENSION_ALERT_KEY, true);
    const [isClipperInstalled, setIsClipperInstalled] = useState(false);

    const sendChromeExtensionMessage = get(['chrome', 'runtime', 'sendMessage'], window);
    const isChrome = isFunction(sendChromeExtensionMessage);

    useEffect(() => {
        if (isChrome) {
            const chromeExtensionId = get(['chromeExtension', 'chromeExtensionId'])(getClientConfig());
            sendChromeExtensionMessage(chromeExtensionId, { message: 'version' }, (reply) => {
                if (reply) setIsClipperInstalled(true);
            });
        } else {
            const browserSettings = getUserBrowserExtensionsSettings(currentUser);
            const browserName = safelyGetUserAgentBrowserName(getUserAgent())?.toLowerCase();
            const EXTENSION_CHECK_EXPIRY = Date.now() + TIMES.DAY * 30;

            const timeStamp = propIn([browserName, 'lastLogin'], browserSettings);

            if (timeStamp && timeStamp < EXTENSION_CHECK_EXPIRY) setIsClipperInstalled(true);
        }
    }, []);

    if (!isPanelVisible || isClipperInstalled || isPlatformIpad(platformDetails)) return null;

    return (
        <li className="ImageSearchWebExtensionAlertPanel">
            <div className="extension-panel-container">
                <ExtensionPanelCloseButton closeFn={() => setIsPanelVisible(false)} />
                <h1 className="extension-panel-title">Add the Web Clipper to your browser</h1>
                <div className="extension-panel-content">
                    <div className="extension-panel-text">
                        <p>
                            Instantly save images, links, videos to your boards. Available for Chrome, Safari &amp;
                            Firefox.
                        </p>
                        <a
                            href={WEB_CLIPPER_DOWNLOAD_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="content-link"
                        >
                            Install it now
                        </a>
                    </div>
                    <div className="extension-illustration">
                        <img
                            alt="web extension image"
                            className="extension-illustration"
                            src={require('./image-search-web-extension-illustration.png')}
                        />
                    </div>
                </div>
            </div>
        </li>
    );
};

ImageSearchWebExtensionAlertPanel.propTypes = {
    currentUser: PropTypes.object,
    platformDetails: PropTypes.object,
};

export default connect(mapStateToProps)(ImageSearchWebExtensionAlertPanel);
